// CatchFilterService - Dexie-based filtering for performance
import { db } from './FishingDB';
import { CatchLog } from '../entities/FishingEntities';
import { CatchFilters } from '../entities/CatchesPageTypes';

/**
 * Filter catches using Dexie queries (much faster than in-memory filtering)
 * Handles large datasets (20,000+ catches) efficiently
 */
export async function filterCatchesWithDexie(filters: CatchFilters): Promise<CatchLog[]> {
  let query = db.catches.toCollection();
  
  // Date range filter (indexed - very fast!)
  if (filters.dateRange) {
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;
    
    let minTimestamp = 0;
    switch (filters.dateRange) {
      case 'today':
        minTimestamp = now - oneDayMs;
        break;
      case 'week':
        minTimestamp = now - 7 * oneDayMs;
        break;
      case 'month':
        minTimestamp = now - 30 * oneDayMs;
        break;
      case 'year':
        minTimestamp = now - 365 * oneDayMs;
        break;
    }
    
    query = db.catches.where('timestamp').aboveOrEqual(minTimestamp);
  }
  
  // Species filter (indexed - fast!)
  if (filters.species.length === 1) {
    // Single species - use index
    query = query.and(c => c.species === filters.species[0]);
  } else if (filters.species.length > 1) {
    // Multiple species - use Set for O(1) lookup
    const speciesSet = new Set(filters.species);
    query = query.and(c => speciesSet.has(c.species));
  }
  
  // Area filter (indexed - fast!)
  if (filters.areas.length > 0) {
    const areasSet = new Set(filters.areas);
    query = query.and(c => !!(c.fishingAreaId && areasSet.has(c.fishingAreaId)));
  }
  
  // Caught/Contact filter
  if (filters.caughtOnly !== null) {
    query = query.and(c => c.caught === filters.caughtOnly);
  }
  
  // Weight filters
  if (filters.minWeight !== undefined) {
    query = query.and(c => !!(c.weight && c.weight >= filters.minWeight!));
  }
  if (filters.maxWeight !== undefined) {
    query = query.and(c => !!(c.weight && c.weight <= filters.maxWeight!));
  }
  
  // Length filters
  if (filters.minLength !== undefined) {
    query = query.and(c => !!(c.length && c.length >= filters.minLength!));
  }
  if (filters.maxLength !== undefined) {
    query = query.and(c => !!(c.length && c.length <= filters.maxLength!));
  }
  
  // Method filters
  if (filters.methods.length > 0) {
    const methodsSet = new Set(filters.methods);
    query = query.and(c => !!(c.method && methodsSet.has(c.method)));
  }
  
  // Tackle/Bait filters (check tackle array)
  if (filters.baits.length > 0) {
    const baitsSet = new Set(filters.baits);
    query = query.and(c => c.tackle?.some(t => baitsSet.has(t)) ?? false);
  }
  
  // Season filter
  if (filters.seasons.length > 0) {
    const seasonsSet = new Set(filters.seasons);
    query = query.and(c => {
      const month = new Date(c.timestamp).getMonth();
      const season = getSeasonFromMonth(month);
      return seasonsSet.has(season);
    });
  }
  
  // Time of day filter
  if (filters.timeOfDay) {
    const { start, end } = filters.timeOfDay;
    query = query.and(c => {
      const hour = new Date(c.timestamp).getHours();
      if (start <= end) {
        return hour >= start && hour <= end;
      } else {
        // Handle overnight range (e.g., 22:00 - 06:00)
        return hour >= start || hour <= end;
      }
    });
  }
  
  // Weather filters (from weatherHistory if available)
  if (filters.moonPhase) {
    query = query.and(c => {
      const moonPhase = c.weatherHistory?.daily?.[0]?.moonPhase ?? c.moonPhase;
      return moonPhase !== undefined && 
             moonPhase >= filters.moonPhase!.min && 
             moonPhase <= filters.moonPhase!.max;
    });
  }
  
  if (filters.minPressure !== undefined) {
    query = query.and(c => !!(c.pressure && c.pressure >= filters.minPressure!));
  }
  if (filters.maxPressure !== undefined) {
    query = query.and(c => !!(c.pressure && c.pressure <= filters.maxPressure!));
  }
  
  if (filters.minTemp !== undefined) {
    query = query.and(c => c.airTemp !== undefined && c.airTemp >= filters.minTemp!);
  }
  if (filters.maxTemp !== undefined) {
    query = query.and(c => c.airTemp !== undefined && c.airTemp <= filters.maxTemp!);
  }
  
  if (filters.minWindSpeed !== undefined) {
    query = query.and(c => c.windSpeed !== undefined && c.windSpeed >= filters.minWindSpeed!);
  }
  if (filters.maxWindSpeed !== undefined) {
    query = query.and(c => c.windSpeed !== undefined && c.windSpeed <= filters.maxWindSpeed!);
  }
  
  // Wind direction filter
  if (filters.windDirections.length > 0) {
    query = query.and(c => {
      if (c.windDirection === undefined) return false;
      const direction = getWindDirectionFromDegrees(c.windDirection);
      return filters.windDirections.includes(direction);
    });
  }
  
  // Search filter (last - most expensive)
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    query = query.and(c => 
      c.species.toLowerCase().includes(searchLower) ||
      (c.notes?.toLowerCase().includes(searchLower) ?? false) ||
      (c.tackle?.some(t => t.toLowerCase().includes(searchLower)) ?? false) ||
      (c.method?.toLowerCase().includes(searchLower) ?? false)
    );
  }
  
  // Execute query and return results
  return await query.toArray();
}

/**
 * Get season from month (0-11)
 */
function getSeasonFromMonth(month: number): string {
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'fall';
  return 'winter';
}

/**
 * Convert wind degrees to cardinal direction
 */
function getWindDirectionFromDegrees(degrees: number): string {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(((degrees % 360) / 45)) % 8;
  return directions[index];
}

/**
 * Get max values from database (for slider ranges)
 */
export async function getFilterMaxValues(): Promise<{
  maxWeight: number;
  maxLength: number;
  maxPressure: number;
  maxTemp: number;
  minTemp: number;
  maxWindSpeed: number;
}> {
  const catches = await db.catches.toArray();
  
  const weights = catches.filter(c => c.weight).map(c => c.weight!);
  const lengths = catches.filter(c => c.length).map(c => c.length!);
  const pressures = catches.filter(c => c.pressure).map(c => c.pressure!);
  const temps = catches.filter(c => c.airTemp !== undefined).map(c => c.airTemp!);
  const windSpeeds = catches.filter(c => c.windSpeed !== undefined).map(c => c.windSpeed!);
  
  return {
    maxWeight: weights.length > 0 ? Math.max(...weights) : 10000,
    maxLength: lengths.length > 0 ? Math.max(...lengths) : 1500,
    maxPressure: pressures.length > 0 ? Math.max(...pressures) : 1050,
    maxTemp: temps.length > 0 ? Math.max(...temps) : 30,
    minTemp: temps.length > 0 ? Math.min(...temps) : -10,
    maxWindSpeed: windSpeeds.length > 0 ? Math.max(...windSpeeds) : 20
  };
}

/**
 * Get unique values for filter options (methods, baits, etc)
 */
export async function getFilterOptions(): Promise<{
  methods: string[];
  baits: string[];
}> {
  const catches = await db.catches.toArray();
  
  const methodsSet = new Set<string>();
  const baitsSet = new Set<string>();
  
  catches.forEach(c => {
    if (c.method) methodsSet.add(c.method);
    if (c.tackle) c.tackle.forEach(t => baitsSet.add(t));
  });
  
  return {
    methods: Array.from(methodsSet).sort(),
    baits: Array.from(baitsSet).sort()
  };
}
